import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../store';
import { agentService, Citation } from '../../services/agentService';
import { twgs as twgApi, default as api } from '../../services/api';
import { CommandAutocomplete } from '../../components/agent/CommandAutocomplete';
import { MentionAutocomplete } from '../../components/agent/MentionAutocomplete';
import EmailApprovalModal, { EmailApprovalRequest, EmailDraft } from '../../components/agent/EmailApprovalModal';
import DocumentApprovalModal from '../../components/modals/DocumentApprovalModal';
import SettingsModal from '../../components/agent/SettingsModal';
import EnhancedMessageBubble from '../../components/agent/EnhancedMessageBubble';
import ThinkingTimeline, { ThinkingStep } from '../../components/agent/ThinkingTimeline';
import WorkspaceContextPanel from '../../components/workspace/WorkspaceContextPanel';
import StreamingChatView from '../../components/agent/StreamingChatView';
import { useAgentStream } from '../../hooks/useAgentStream';
import { tryParseConfirm, executeConfirm } from '../../utils/confirmAction';
import { CommandAutocompleteResult } from '../../types/agent';
import { UserRole } from '../../types/auth';

interface Message {
    id: string;
    role: 'user' | 'agent';
    content: string;
    timestamp: Date;
    citations?: Citation[];
    reactions?: MessageReaction[];
    agentName?: string;
    agentIcon?: string;
    approvalRequest?: any;
    suggestions?: string[];
}

interface MessageReaction {
    emoji: string;
    count: number;
    users: string[];
}

interface AgentMentionSuggestion {
    mention: string;
    agent_id: string;
    name: string;
    icon: string;
    description: string;
    match_score?: number;
}

export default function TwgAgent() {
    const user = useSelector((state: RootState) => state.auth.user);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState<string | undefined>();
    const [activeTwg, setActiveTwg] = useState<{ id: string; name: string } | null>(null);
    const [showTwgSwitcher, setShowTwgSwitcher] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const twgSwitcherRef = useRef<HTMLDivElement>(null);

    const { id } = useParams<{ id: string }>();

    // Initialize Active TWG Context
    useEffect(() => {
        const initContext = async () => {
            if (!user) return;

            // 1. Priority: URL Param (Direct Link or navigation)
            if (id) {
                // Check if we have it in user's assigned TWGs (saves API call)
                const assignedTwg = user.twgs?.find(t => t.id === id);
                if (assignedTwg) {
                    setActiveTwg({ id: assignedTwg.id, name: assignedTwg.name });
                    return;
                }

                // If not assigned (e.g. Admin or public), fetch details
                try {
                    const res = await twgApi.get(id);
                    setActiveTwg({ id: res.data.id, name: res.data.name });
                } catch (err) {
                    console.error("Failed to load TWG context from URL", err);
                    // Fallback to safe default based on role
                }
            } else {
                // 2. Fallback: Role-based Default
                if (user.role === UserRole.ADMIN || user.role === UserRole.SECRETARIAT_LEAD) {
                    setActiveTwg({ id: 'secretariat', name: 'Secretariat' });
                } else if (user.twgs && user.twgs.length > 0) {
                    // Default to first assigned TWG
                    setActiveTwg({ id: user.twgs[0].id, name: user.twgs[0].name });
                } else if (user.twg_ids && user.twg_ids.length > 0) {
                    // Fallback: If full TWG objects missing, fetch by ID
                    try {
                        console.log('[TwgAgent] Fetching default TWG details for ID:', user.twg_ids[0]);
                        const res = await twgApi.get(user.twg_ids[0]);
                        setActiveTwg({ id: res.data.id, name: res.data.name });
                    } catch (err) {
                        console.error("Failed to fetch default TWG details", err);
                    }
                }
            }
        };

        initContext();
    }, [user, id]);


    // Autocomplete state
    const [commandSuggestions, setCommandSuggestions] = useState<CommandAutocompleteResult[]>([]);
    const [mentionSuggestions, setMentionSuggestions] = useState<AgentMentionSuggestion[]>([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
    const [autocompleteType, setAutocompleteType] = useState<'command' | 'mention' | null>(null);

    // Modals
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [emailApprovalRequest, setEmailApprovalRequest] = useState<EmailApprovalRequest | null>(null);
    const [documentApprovalRequest, setDocumentApprovalRequest] = useState<any | null>(null);

    // Context panel state
    const [showContextPanel, setShowContextPanel] = useState(true);
    const [showOverflow, setShowOverflow] = useState(false);
    const overflowRef = useRef<HTMLDivElement>(null);

    // Typing state
    const [typingMessage, setTypingMessage] = useState<string | null>(null);
    const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
    const [streamStartTime, setStreamStartTime] = useState<number>(0);

    // Claude Code-style streaming hook
    const {
        messages: streamEvents,
        isStreaming,
        streamingText,
        conversationId: streamConvId,
        sendMessage: sendStreamMessage,
        cancel: cancelStream,
    } = useAgentStream();

    // ========================================================================
    // CHAT HISTORY PERSISTENCE (localStorage)
    // ========================================================================
    const getChatStorageKey = (twgId: string) => `martin_chat_${twgId}`;

    // Load chat history when activeTwg changes
    useEffect(() => {
        if (!activeTwg?.id) return;

        const storageKey = getChatStorageKey(activeTwg.id);
        try {
            const savedData = localStorage.getItem(storageKey);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                // Restore messages with proper Date objects
                const restoredMessages: Message[] = parsed.messages.map((m: any) => ({
                    ...m,
                    timestamp: new Date(m.timestamp)
                }));
                setMessages(restoredMessages);
                setConversationId(parsed.conversationId);
                console.log(`[TwgAgent] Restored ${restoredMessages.length} messages from localStorage`);
            }
        } catch (error) {
            console.error('[TwgAgent] Failed to restore chat history:', error);
        }
    }, [activeTwg?.id]);

    // Save chat history when messages change
    useEffect(() => {
        if (!activeTwg?.id || messages.length === 0) return;

        const storageKey = getChatStorageKey(activeTwg.id);
        try {
            const dataToSave = {
                messages: messages,
                conversationId: conversationId,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        } catch (error) {
            console.error('[TwgAgent] Failed to save chat history:', error);
        }
    }, [messages, conversationId, activeTwg?.id]);
    // ========================================================================


    // Helper to get Martin Persona Name

    // Document Approval Handlers
    const handleDocumentApprovalResolve = async (approved: boolean, result?: any) => {
        setDocumentApprovalRequest(null);

        if (!conversationId) return;

        // Resume the agent loop
        try {
            setIsLoading(true);
            await agentService.chatStream({
                message: approved
                    ? `I have approved the document. The ID is ${result?.document_id}. Please proceed.`
                    : "I have declined the document creation.",
                conversation_id: conversationId,
                twg_id: activeTwg?.id
            }, {
                onThinking: (status) => setTypingMessage(status), // Using setTypingMessage as setCurrentThought is not defined
                onResponse: (response) => {
                    // Update the last message if it's from agent, or add new
                    setMessages(prev => {
                        const newMsg: Message = {
                            id: crypto.randomUUID(),
                            role: 'agent',
                            content: response.content || response,
                            timestamp: new Date(),
                            agentName: currentAgentName, // Use dynamic identity
                            citations: response.citations || [],
                            suggestions: response.suggestions
                        };
                        return [...prev, newMsg];
                    });
                    // Clear thought when response comes
                    setTypingMessage(null); // Using setTypingMessage as setCurrentThought is not defined
                },
                onDone: () => setIsLoading(false),
                onError: (err) => {
                    console.error("Error resuming after doc approval", err);
                    setIsLoading(false);
                }
            });
        } catch (error) {
            console.error("Failed to resume conversation", error);
            setIsLoading(false);
        }
    };

    const getAgentIdentity = (twgName?: string) => {
        if (!twgName) return "Martin Copilot";

        // Normalize
        const name = twgName.toLowerCase();

        if (name.includes('secretariat')) return "Secretariat Martin";
        if (name.includes('leads council') || name.includes('council')) return "Council Martin";
        if (name.includes('energy')) return "Energy Martin";
        if (name.includes('agriculture') || name.includes('agribusiness')) return "Agriculture Martin";
        if (name.includes('minerals')) return "Minerals Martin";
        if (name.includes('digital')) return "Digital Martin";
        if (name.includes('protocol')) return "Protocol Martin";
        if (name.includes('resource') || name.includes('mobilization')) return "Investment Martin";

        // Fallback for custom/new TWGs
        return `${twgName} Martin`;
    };

    const currentAgentName = getAgentIdentity(activeTwg?.name);

    const getTwgIcon = (twgName?: string): string => {
        if (!twgName) return 'smart_toy';
        const name = twgName.toLowerCase();
        if (name.includes('energy')) return 'bolt';
        if (name.includes('agriculture') || name.includes('agribusiness')) return 'agriculture';
        if (name.includes('minerals')) return 'diamond';
        if (name.includes('digital')) return 'devices';
        if (name.includes('protocol')) return 'local_shipping';
        if (name.includes('resource') || name.includes('mobilization')) return 'payments';
        if (name.includes('secretariat')) return 'shield_person';
        if (name.includes('council')) return 'groups';
        return 'smart_toy';
    };

    const getTwgColor = (twgName?: string): string => {
        if (!twgName) return 'from-teal-600 to-teal-700';
        const name = twgName.toLowerCase();
        if (name.includes('energy')) return 'from-amber-500 to-orange-600';
        if (name.includes('agriculture') || name.includes('agribusiness')) return 'from-green-500 to-emerald-600';
        if (name.includes('minerals')) return 'from-cyan-500 to-teal-600';
        if (name.includes('digital')) return 'from-teal-500 to-teal-700';
        if (name.includes('protocol')) return 'from-rose-500 to-pink-600';
        if (name.includes('resource') || name.includes('mobilization')) return 'from-teal-500 to-cyan-600';
        if (name.includes('secretariat')) return 'from-teal-600 to-teal-700';
        if (name.includes('council')) return 'from-slate-600 to-gray-700';
        return 'from-teal-600 to-teal-700';
    };

    // Click-outside handler to close TWG switcher dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (twgSwitcherRef.current && !twgSwitcherRef.current.contains(event.target as Node)) {
                setShowTwgSwitcher(false);
            }
        };
        if (showTwgSwitcher) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTwgSwitcher]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
                setShowOverflow(false);
            }
        };
        if (showOverflow) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showOverflow]);

    const handleTwgSwitch = (twg: { id: string; name: string }) => {
        setActiveTwg({ id: twg.id, name: twg.name });
        setShowTwgSwitcher(false);
        setConversationId(undefined);
        setMessages([]);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };


    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading, typingMessage, thinkingSteps]);

    // When the stream finishes, promote the done-event response to the messages list
    const prevIsStreamingRef = useRef(false);
    useEffect(() => {
        const wasStreaming = prevIsStreamingRef.current;
        prevIsStreamingRef.current = isStreaming;

        if (wasStreaming && !isStreaming) {
            // Find the done event
            const doneEvt = streamEvents.find(m => m.event.type === 'done');
            if (doneEvt && doneEvt.event.type === 'done') {
                const done = doneEvt.event;
                let content = done.response;
                let suggestions: string[] = [];

                // Parse embedded suggestions
                if (content.includes('<<SUGGESTIONS>>')) {
                    try {
                        const start = content.indexOf('<<SUGGESTIONS>>');
                        const end = content.indexOf('<</SUGGESTIONS>>');
                        if (start !== -1 && end !== -1) {
                            const jsonStr = content.substring(start + 15, end);
                            suggestions = JSON.parse(jsonStr);
                            content = content.substring(0, start).trim();
                        }
                    } catch (_) { /* ignore */ }
                }

                const agentMessage: Message = {
                    id: Date.now().toString(),
                    role: 'agent',
                    content,
                    timestamp: new Date(),
                    agentName: currentAgentName,
                    citations: (done.citations as Citation[]) || [],
                    suggestions,
                };
                setMessages(prev => [...prev, agentMessage]);
                if (done.conversation_id) setConversationId(done.conversation_id);
            }

            // Also find error events
            const errEvt = streamEvents.find(m => m.event.type === 'error');
            if (errEvt && errEvt.event.type === 'error' && !doneEvt) {
                setMessages(prev => [...prev, {
                    id: Date.now().toString(),
                    role: 'agent' as const,
                    content: `Sorry, I encountered an error: ${(errEvt.event as { message: string }).message}`,
                    timestamp: new Date(),
                    agentName: currentAgentName,
                }]);
            }

            setIsLoading(false);
            setTypingMessage(null);
        }
    }, [isStreaming, streamEvents, currentAgentName]);

    // Sync streamConvId back to conversationId
    useEffect(() => {
        if (streamConvId) setConversationId(streamConvId);
    }, [streamConvId]);

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading || isStreaming) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputMessage;
        setInputMessage('');
        setIsLoading(true);
        setThinkingSteps([]);
        setStreamStartTime(Date.now());
        setTypingMessage(null);

        const twgIdToSend = activeTwg?.id !== 'secretariat' ? activeTwg?.id : undefined;

        // Use Claude Code-style GET stream via useAgentStream
        sendStreamMessage({
            message: messageToSend,
            conversationId,
            twgId: twgIdToSend,
        });
    };

    // Detect slash commands and @mentions in input
    const handleInputChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputMessage(value);

        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);

        // Check for command trigger (/)
        const commandMatch = textBeforeCursor.match(/\/(\w*)$/);
        if (commandMatch) {
            const query = '/' + commandMatch[1];
            try {
                const response = await api.get(`/agents/commands/autocomplete`, {
                    params: { query }
                });
                setCommandSuggestions(response.data.suggestions);
                setAutocompleteType('command');
                setSelectedSuggestionIndex(0);
                return;
            } catch (error) {
                console.error('Error fetching command suggestions:', error);
            }
        }

        // Check for mention trigger (@) - RESTRICTED TO ADMINS
        const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
        if (mentionMatch && user?.role === UserRole.ADMIN) {
            const query = '@' + mentionMatch[1];
            try {
                const response = await api.get(`/agents/mentions/autocomplete`, {
                    params: { query }
                });
                setMentionSuggestions(response.data.suggestions);
                setAutocompleteType('mention');
                setSelectedSuggestionIndex(0);
                return;
            } catch (error) {
                console.error('Error fetching mention suggestions:', error);
            }
        }

        // Clear autocomplete if no trigger detected
        setAutocompleteType(null);
        setCommandSuggestions([]);
        setMentionSuggestions([]);
    };

    const handleCommandSelect = (suggestion: CommandAutocompleteResult) => {
        const cursorPosition = inputRef.current?.selectionStart || 0;
        const textBeforeCursor = inputMessage.substring(0, cursorPosition);
        const textAfterCursor = inputMessage.substring(cursorPosition);

        const commandMatch = textBeforeCursor.match(/\/(\w*)$/);
        if (commandMatch) {
            const beforeCommand = textBeforeCursor.substring(0, textBeforeCursor.length - commandMatch[0].length);
            const newValue = beforeCommand + suggestion.command + ' ' + textAfterCursor;
            setInputMessage(newValue);
            setAutocompleteType(null);
            setCommandSuggestions([]);

            setTimeout(() => {
                if (inputRef.current) {
                    const newCursorPos = beforeCommand.length + suggestion.command.length + 1;
                    inputRef.current.focus();
                    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        }
    };

    const handleMentionSelect = (suggestion: AgentMentionSuggestion) => {
        const cursorPosition = inputRef.current?.selectionStart || 0;
        const textBeforeCursor = inputMessage.substring(0, cursorPosition);
        const textAfterCursor = inputMessage.substring(cursorPosition);

        const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
        if (mentionMatch) {
            const beforeMention = textBeforeCursor.substring(0, textBeforeCursor.length - mentionMatch[0].length);
            const newValue = beforeMention + suggestion.mention + ' ' + textAfterCursor;
            setInputMessage(newValue);
            setAutocompleteType(null);
            setMentionSuggestions([]);

            setTimeout(() => {
                if (inputRef.current) {
                    const newCursorPos = beforeMention.length + suggestion.mention.length + 1;
                    inputRef.current.focus();
                    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (autocompleteType) {
            const suggestions = autocompleteType === 'command' ? commandSuggestions : mentionSuggestions;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedSuggestionIndex((prev) => (prev + 1) % suggestions.length);
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedSuggestionIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
                return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (autocompleteType === 'command' && commandSuggestions[selectedSuggestionIndex]) {
                    handleCommandSelect(commandSuggestions[selectedSuggestionIndex]);
                } else if (autocompleteType === 'mention' && mentionSuggestions[selectedSuggestionIndex]) {
                    handleMentionSelect(mentionSuggestions[selectedSuggestionIndex]);
                }
                return;
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                setAutocompleteType(null);
                setCommandSuggestions([]);
                setMentionSuggestions([]);
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleClearConversation = () => {
        if (messages.length === 0 && !isLoading && !isStreaming) return;

        if (window.confirm('Are you sure you want to clear this conversation? This action cannot be undone.')) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            cancelStream();

            setMessages([]);
            setConversationId(undefined);
            setIsLoading(false);
            setTypingMessage(null);

            // Also clear from localStorage
            if (activeTwg?.id) {
                const storageKey = getChatStorageKey(activeTwg.id);
                localStorage.removeItem(storageKey);
                console.log(`[TwgAgent] Cleared chat history from localStorage for ${activeTwg.id}`);
            }
        }
    };

    const handleReact = (messageId: string, emoji: string) => {
        setMessages(prev => prev.map(msg => {
            if (msg.id === messageId) {
                const reactions = msg.reactions || [];
                const existingReaction = reactions.find(r => r.emoji === emoji);

                if (existingReaction) {
                    return {
                        ...msg,
                        reactions: reactions.map(r =>
                            r.emoji === emoji
                                ? { ...r, count: r.count + 1, users: [...r.users, 'You'] }
                                : r
                        )
                    };
                } else {
                    return {
                        ...msg,
                        reactions: [...reactions, { emoji, count: 1, users: ['You'] }]
                    };
                }
            }
            return msg;
        }));
    };

    const handleInsertContext = (contextType: string, data: any) => {
        let contextText = '';

        switch (contextType) {
            case 'meeting':
                contextText = `Please summarize the meeting "${data.title}" scheduled for ${data.date}`;
                break;
            case 'action':
                contextText = `What is the status of the action item: "${data.task}" assigned to ${data.assignee}?`;
                break;
            case 'document':
                contextText = `Can you provide information about the document "${data.name}"?`;
                break;
            case 'template':
                if (data.type === 'summary') {
                    contextText = 'Generate a summary of all recent TWG activities';
                } else if (data.type === 'stats') {
                    contextText = 'Show me the statistics for this TWG workspace';
                }
                break;
            default:
                contextText = `Context: ${JSON.stringify(data)}`;
        }

        setInputMessage(contextText);
        inputRef.current?.focus();
    };

    const handleEmailApprovalResolve = async (approved: boolean, requestId: string, modifications?: EmailDraft, declineReason?: string) => {
        try {
            if (approved) {
                const approvalData = {
                    request_id: requestId,
                    approved: true,
                    modifications: modifications || null
                };
                const result = await agentService.approveEmail(requestId, approvalData);
                const successMessage: Message = {
                    id: Date.now().toString(),
                    role: 'agent',
                    content: result.email_sent
                        ? `✅ Email sent successfully! Message ID: ${result.message_id}`
                        : `⚠️ ${result.message}`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, successMessage]);
            } else {
                await agentService.declineEmail(requestId, declineReason);
                const declineMessage: Message = {
                    id: Date.now().toString(),
                    role: 'agent',
                    content: `🚫 Email sending cancelled: ${declineReason || 'Declined by user'}`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, declineMessage]);
            }
        } catch (error) {
            console.error('Error handling email approval:', error);
            const errorMessage: Message = {
                id: Date.now().toString(),
                role: 'agent',
                content: `❌ Failed to process email approval: ${error}`,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setEmailApprovalRequest(null);
        }
    };


    const handleSuggestionClick = (suggestion: string) => {
        setInputMessage(suggestion);
        // Using strict timeout to ensure state update before firing
        setTimeout(() => {
            handleSendMessage();
        }, 50);
    };

    // Resolve a confirm-then-execute card (e.g. create_action_item) and append
    // the result as an agent message so the TWG chat mirrors the GlobalCopilot.
    const [resolvedCards, setResolvedCards] = useState<Record<string, 'success' | 'cancelled'>>({});
    const handleConfirmAction = async (
        card: ReturnType<typeof tryParseConfirm>,
        confirmed: boolean,
    ) => {
        if (!card) return;
        const result = await executeConfirm(card, confirmed).catch(
            (e) => `Action failed: ${e?.message || e}`,
        );
        setResolvedCards((prev) => ({ ...prev, [card.action_id]: confirmed ? 'success' : 'cancelled' }));
        setMessages((prev) => [
            ...prev,
            {
                id: `confirm-${card.action_id}-${confirmed ? 'ok' : 'no'}`,
                role: 'agent',
                content: result,
                timestamp: new Date(),
            },
        ]);
    };

    const [hoveredChip, setHoveredChip] = useState<string | null>(null);

    return (
        <div style={{ fontFamily: "'Geist', 'Inter', system-ui, sans-serif", color: 'var(--ink-900)', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>


            {/* Main Content */}
            <main className="flex-1 flex overflow-hidden" style={{ maxWidth: 1520, margin: '0 auto', width: '100%' }}>
                {/* Chat Area */}
                <div className="flex-1 flex flex-col bg-transparent">
                    {/* Agent Header */}
                    <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <div className="relative flex items-center gap-3" ref={twgSwitcherRef}>
                            <div className="relative">
                                <div className={`size-10 rounded-full bg-gradient-to-br ${getTwgColor(activeTwg?.name)} flex items-center justify-center text-white`}>
                                    <span className="material-symbols-outlined">{getTwgIcon(activeTwg?.name)}</span>
                                </div>
                                <span className="absolute bottom-0 right-0 size-3 border-2 border-white dark:border-[#1a202c] bg-green-500 rounded-full"></span>
                            </div>
                            <div>
                                {user?.twgs && user.twgs.length > 1 ? (
                                    <button
                                        onClick={() => setShowTwgSwitcher(!showTwgSwitcher)}
                                        className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                                    >
                                        <h3 className="font-display text-[#0d121b] dark:text-white" style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                            {currentAgentName}
                                        </h3>
                                        <span className={`material-symbols-outlined text-[18px] text-[#4c669a] transition-transform ${showTwgSwitcher ? 'rotate-180' : ''}`}>
                                            expand_more
                                        </span>
                                    </button>
                                ) : (
                                    <h3 className="font-display text-[#0d121b] dark:text-white" style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                        {currentAgentName}
                                    </h3>
                                )}
                                <p className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                                    Online · {activeTwg?.name || 'Secretariat'}
                                </p>
                            </div>

                            {/* TWG Switcher Dropdown */}
                            {showTwgSwitcher && user?.twgs && user.twgs.length > 1 && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 8, width: 288, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 50, overflow: 'hidden' }}>
                                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                                        <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.14em', margin: 0, fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}>Switch Agent</p>
                                    </div>
                                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                        {user.twgs.map((twg) => {
                                            const isActive = activeTwg?.id === twg.id;
                                            return (
                                                <button
                                                    key={twg.id}
                                                    onClick={() => handleTwgSwitch(twg)}
                                                    style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, background: isActive ? 'var(--accent-soft)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--accent-soft)'; }}
                                                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                >
                                                    <div className={`size-9 rounded-full bg-gradient-to-br ${getTwgColor(twg.name)} flex items-center justify-center text-white flex-shrink-0`}>
                                                        <span className="material-symbols-outlined text-[18px]">{getTwgIcon(twg.name)}</span>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                                        <p style={{ fontSize: 13, fontWeight: 500, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? 'var(--accent)' : 'var(--ink-900)', fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}>
                                                            {getAgentIdentity(twg.name)}
                                                        </p>
                                                        <p style={{ fontSize: 11, color: 'var(--ink-500)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}>
                                                            {twg.name}
                                                        </p>
                                                    </div>
                                                    {isActive && (
                                                        <span className="material-symbols-outlined text-[20px] flex-shrink-0" style={{ color: 'var(--accent)' }}>check_circle</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setShowContextPanel(!showContextPanel)}
                                className={`clickable-scale p-2 rounded-lg transition-colors ${showContextPanel
                                    ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400'
                                    : 'text-[#4c669a] hover:bg-gray-100 dark:hover:bg-[#2d3748]'
                                    }`}
                                title="Toggle workspace context"
                            >
                                <span className="material-symbols-outlined">view_sidebar</span>
                            </button>
                            {(isLoading || isStreaming) && (
                                <button
                                    onClick={() => {
                                        if (abortControllerRef.current) {
                                            abortControllerRef.current.abort();
                                            abortControllerRef.current = null;
                                        }
                                        cancelStream();
                                        setIsLoading(false);
                                        setTypingMessage(null);
                                    }}
                                    className="clickable-scale p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    title="Stop generation"
                                >
                                    <span className="material-symbols-outlined">stop_circle</span>
                                </button>
                            )}
                            <div className="relative" ref={overflowRef}>
                                <button
                                    onClick={() => setShowOverflow(!showOverflow)}
                                    className="clickable-scale p-2 text-[#4c669a] hover:bg-gray-100 dark:hover:bg-[#2d3748] rounded-lg transition-colors"
                                    title="More options"
                                >
                                    <span className="material-symbols-outlined">more_horiz</span>
                                </button>
                                {showOverflow && (
                                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, width: 208, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 50, overflow: 'hidden', padding: '4px 0' }}>
                                        <button
                                            onClick={() => { setSettingsOpen(true); setShowOverflow(false); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0d121b] dark:text-white hover:bg-gray-50 dark:hover:bg-[#2d3748] transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-[#4c669a]">settings</span>
                                            Settings
                                        </button>
                                        <button
                                            onClick={() => { handleClearConversation(); setShowOverflow(false); }}
                                            disabled={messages.length === 0 && !isLoading}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                            Clear conversation
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 bg-transparent">
                        {messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center px-4">
                                <div className="max-w-2xl w-full text-center space-y-6">
                                    <div>
                                        <h2 className="font-display" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>
                                            Good morning, {user?.full_name ? user.full_name.split(' ')[0] : 'there'}
                                        </h2>
                                        <p style={{ fontSize: 13, color: 'var(--ink-500)', marginTop: 8, fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}>
                                            How can {currentAgentName} help you today?
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 justify-center">
                                        {[
                                            { label: 'Draft minutes', prompt: 'Draft minutes for the last meeting' },
                                            { label: 'Summarize session', prompt: "Summarize the last session's key outcomes" },
                                            { label: 'Check availability', prompt: 'Check availability for the next board meeting' },
                                            { label: 'Find documents', prompt: 'Find recent documents from this TWG' },
                                            { label: 'Action items', prompt: 'List all open action items from the last meeting' },
                                            { label: 'Prepare agenda', prompt: 'Help me prepare an agenda for the next meeting' },
                                        ].map(({ label, prompt }) => (
                                            <button
                                                key={label}
                                                onClick={() => { setInputMessage(prompt); inputRef.current?.focus(); }}
                                                onMouseEnter={() => setHoveredChip(label)}
                                                onMouseLeave={() => setHoveredChip(null)}
                                                style={{ border: '1px solid var(--border)', borderRadius: 20, padding: '8px 16px', background: hoveredChip === label ? 'var(--accent-soft)' : 'var(--surface)', fontSize: 13, color: hoveredChip === label ? 'var(--accent)' : 'var(--ink-700)', cursor: 'pointer', fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            messages.map((message) => {
                                const confirmCard = message.role === 'agent' ? tryParseConfirm(message.content) : null;
                                if (confirmCard) {
                                    const resolution = resolvedCards[confirmCard.action_id];
                                    return (
                                        <div key={message.id} style={{
                                            border: '1px solid var(--border)', background: 'var(--ink-50)',
                                            borderRadius: 8, padding: 12, maxWidth: 520,
                                        }}>
                                            <div style={{
                                                fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600,
                                                color: confirmCard.irreversible ? 'var(--terra)' : 'var(--accent)', marginBottom: 6,
                                            }}>
                                                {confirmCard.irreversible ? 'Irreversible Action' : 'Confirm Action'}
                                            </div>
                                            <div style={{ fontSize: 13, color: 'var(--ink-900)', marginBottom: 10 }}>
                                                {confirmCard.summary}
                                            </div>
                                            {resolution ? (
                                                <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                                                    {resolution === 'success' ? 'Done.' : 'Cancelled.'}
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <button
                                                        onClick={() => handleConfirmAction(confirmCard, true)}
                                                        style={{
                                                            background: 'var(--accent)', color: '#fff', border: 'none',
                                                            borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                                                        }}
                                                    >Confirm</button>
                                                    <button
                                                        onClick={() => handleConfirmAction(confirmCard, false)}
                                                        style={{
                                                            background: 'transparent', color: 'var(--ink-500)',
                                                            border: '1px solid var(--border)', borderRadius: 6,
                                                            padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                                                        }}
                                                    >Cancel</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <EnhancedMessageBubble
                                        key={message.id}
                                        message={{ ...message, approvalRequest: message.approvalRequest }}
                                        onReact={handleReact}
                                        onApprove={message.approvalRequest ? (id, mods) => handleEmailApprovalResolve(true, id, mods) : undefined}
                                        onDecline={message.approvalRequest ? (id) => handleEmailApprovalResolve(false, id) : undefined}
                                        onSuggestionClick={handleSuggestionClick}
                                    />
                                );
                            })
                        )}
                        {(isLoading || thinkingSteps.length > 0) && !isStreaming && (
                            <ThinkingTimeline
                                steps={thinkingSteps}
                                isComplete={!isLoading && thinkingSteps.length > 0}
                                startTime={streamStartTime}
                                agentName={currentAgentName}
                            />
                        )}

                        {/* Claude Code-style streaming events view */}
                        {isStreaming && (
                            <StreamingChatView
                                messages={streamEvents}
                                isStreaming={isStreaming}
                                streamingText={streamingText}
                                activeAgentLabel={currentAgentName}
                            />
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 16px 16px', flexShrink: 0 }}>
                        <div className="relative flex items-center gap-2 focus-within:border-[var(--accent)] dark:focus-within:border-[var(--accent)] transition-colors" style={{ border: '1px solid var(--border)', borderRadius: 24, padding: '8px 16px', background: 'var(--surface)' }}>
                            {autocompleteType === 'command' && commandSuggestions.length > 0 && (
                                <CommandAutocomplete
                                    suggestions={commandSuggestions}
                                    selectedIndex={selectedSuggestionIndex}
                                    onSelect={handleCommandSelect}
                                    onHover={setSelectedSuggestionIndex}
                                />
                            )}

                            {autocompleteType === 'mention' && mentionSuggestions.length > 0 && (
                                <MentionAutocomplete
                                    suggestions={mentionSuggestions}
                                    selectedIndex={selectedSuggestionIndex}
                                    onSelect={handleMentionSelect}
                                    onHover={setSelectedSuggestionIndex}
                                />
                            )}

                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                disabled={isLoading || isStreaming}
                                className="flex-1 bg-transparent border-none outline-none focus:ring-0 text-sm py-1.5 min-h-[28px] max-h-32 resize-none text-[#0d121b] dark:text-white placeholder:text-[#9ca3af] disabled:opacity-50"
                                placeholder="Ask anything… or use / for commands"
                            />
                            <button
                                className="clickable-scale p-1.5 text-[#9ca3af] hover:text-[#6b7280] dark:hover:text-[#d1d5db] transition-colors flex-shrink-0"
                                title="Attach file"
                            >
                                <span className="material-symbols-outlined text-[20px]">attach_file</span>
                            </button>
                            <button
                                onClick={handleSendMessage}
                                disabled={!inputMessage.trim() || isLoading || isStreaming}
                                className="clickable-scale p-1.5 text-white rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                                style={{ background: 'var(--accent)' }}
                                title="Send message"
                            >
                                <span className="material-symbols-outlined text-[18px]">arrow_upward</span>
                            </button>
                        </div>
                        <p className="text-[10px] text-[#d1d5db] dark:text-[#4b5563] text-center mt-2">↵ to send · / for commands · @ to mention an agent</p>
                    </div>
                </div>

                {/* Workspace Context Panel */}
                {showContextPanel && (
                    <WorkspaceContextPanel
                        twgName={activeTwg?.name || 'Loading...'}
                        twgId={activeTwg?.id}
                        onInsertContext={handleInsertContext}
                    />
                )}
            </main>

            {/* Modals */}
            {/* Modals */}
            {settingsOpen && (
                <SettingsModal
                    onClose={() => setSettingsOpen(false)}
                />
            )}

            {/* Email Approval Modal */}
            {/* Email Approval Modal */}
            {emailApprovalRequest && (
                <EmailApprovalModal
                    approvalRequest={emailApprovalRequest}
                    onApprove={(id, mods) => handleEmailApprovalResolve(true, id, mods)}
                    onDecline={(id, reason) => handleEmailApprovalResolve(false, id, undefined, reason)}
                    onClose={() => setEmailApprovalRequest(null)}
                />
            )}

            {/* Document Approval Modal */}
            {documentApprovalRequest && (
                <DocumentApprovalModal
                    approvalRequest={documentApprovalRequest}
                    onResolve={handleDocumentApprovalResolve}
                />
            )}
        </div>
    );
}
